import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { NeedsSetup } from "@/components/NeedsSetup";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  FileText, Upload, Trash2, CheckCircle, Clock, AlertCircle,
  File, Loader2, Camera, ScanText, Eye, Lock, Download, ShieldCheck,
  BadgeCheck, Building2, HelpCircle
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUP_ICONS = {
  "📜": "📜", "📐": "📐", "🏛️": "🏛️", "💰": "💰", "🔄": "🔄", "📁": "📁"
};

const TIER_META = {
  1: { label: "Critical", cls: "bg-red-50 text-red-700 border-red-100" },
  2: { label: "Important", cls: "bg-amber-50 text-amber-700 border-amber-100" },
  3: { label: "Supporting", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export const Documents = () => {
  const [searchParams] = useSearchParams();
  const societyIdFromUrl = searchParams.get('society');

  const [vault, setVault] = useState(null);
  const [society, setSociety] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isTeam, setIsTeam] = useState(false); // MangoHouse team (privileged admin)
  const [isAdminManaging, setIsAdminManaging] = useState(false);

  // upload dialog
  const [activeSlot, setActiveSlot] = useState(null);
  const [docName, setDocName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [captureType, setCaptureType] = useState("file");
  const [tosAccepted, setTosAccepted] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // ocr text dialog
  const [textViewDoc, setTextViewDoc] = useState(null);
  const [textDialogOpen, setTextDialogOpen] = useState(false);
  const [ocrProcessing, setOcrProcessing] = useState(null);
  const [leadSending, setLeadSending] = useState(false);

  useEffect(() => { fetchData(); }, [societyIdFromUrl]);

  const fetchData = async () => {
    try {
      const meRes = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (meRes.ok) {
        const me = await meRes.json();
        setIsTeam(!!(me.is_master_admin || me.is_platform_admin || me.is_sales_admin));
      }

      let soc = null;
      if (societyIdFromUrl) {
        const socRes = await fetch(`${API}/societies/${societyIdFromUrl}`, { credentials: "include" });
        if (socRes.ok) { soc = await socRes.json(); setIsAdminManaging(true); }
        else { toast.error("Society not found or access denied"); }
      } else {
        const socRes = await fetch(`${API}/societies`, { credentials: "include" });
        const societies = await socRes.json();
        if (societies.length > 0) soc = societies[0];
      }
      setSociety(soc);
      if (soc) await fetchVault(soc.society_id);
    } catch (e) {
      toast.error("Failed to load document vault");
    } finally {
      setLoading(false);
    }
  };

  const fetchVault = async (sid) => {
    const res = await fetch(`${API}/societies/${sid}/vault`, { credentials: "include" });
    if (res.ok) setVault(await res.json());
  };

  const openUpload = (slot) => {
    setActiveSlot(slot);
    setDocName("");
    setSelectedFile(null);
    setPreviewUrl(null);
    setCaptureType("file");
    setTosAccepted(false);
  };

  const handleFileSelect = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setCaptureType(type);
    if (!docName) setDocName(file.name.replace(/\.[^/.]+$/, ""));
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || !activeSlot) { toast.error("Please choose a file"); return; }
    if (!tosAccepted) { toast.error("Please accept the storage terms of use to upload"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", selectedFile);
      fd.append("name", docName || activeSlot.label);
      fd.append("doc_type", activeSlot.key);
      fd.append("capture_type", captureType);
      const res = await fetch(`${API}/societies/${society.society_id}/documents`, {
        method: "POST", body: fd, credentials: "include"
      });
      if (res.ok) {
        toast.success("Document uploaded");
        setActiveSlot(null);
        await fetchVault(society.society_id);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to upload document");
      }
    } catch (e) {
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    const res = await fetch(`${API}/societies/${society.society_id}/documents/${docId}`, {
      method: "DELETE", credentials: "include"
    });
    if (res.ok) { toast.success("Document deleted"); fetchVault(society.society_id); }
    else toast.error("Failed to delete document");
  };

  const handleSetStatus = async (docId, status) => {
    const res = await fetch(`${API}/societies/${society.society_id}/documents/${docId}/status`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }), credentials: "include"
    });
    if (res.ok) { toast.success(`Marked ${status.replace("_", " ")}`); fetchVault(society.society_id); }
    else { const e = await res.json(); toast.error(e.detail || "Failed to update status"); }
  };

  const handleToggleApplicable = async (slot, applicable) => {
    const res = await fetch(`${API}/societies/${society.society_id}/vault/slot/${slot.key}/applicable`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applicable }), credentials: "include"
    });
    if (res.ok) fetchVault(society.society_id);
    else toast.error("Failed to update");
  };

  const handleRunOCR = async (docId) => {
    setOcrProcessing(docId);
    try {
      const res = await fetch(`${API}/societies/${society.society_id}/documents/${docId}/ocr`, {
        method: "POST", credentials: "include"
      });
      if (res.ok) {
        const result = await res.json();
        toast.success("Text extracted");
        setTextViewDoc({ document_id: docId, extracted_text: result.extracted_text });
        setTextDialogOpen(true);
      } else { const e = await res.json(); toast.error(e.detail || "OCR failed"); }
    } catch (e) { toast.error("OCR failed"); }
    finally { setOcrProcessing(null); }
  };

  const handleViewText = async (docId) => {
    const res = await fetch(`${API}/societies/${society.society_id}/documents/${docId}/text`, { credentials: "include" });
    if (res.ok) { setTextViewDoc(await res.json()); setTextDialogOpen(true); }
    else toast.error("Failed to load text");
  };

  const handleDownload = (docId) => {
    window.open(`${API}/societies/${society.society_id}/documents/${docId}/file`, "_blank");
  };

  const handleTalkToTeam = async () => {
    setLeadSending(true);
    try {
      const res = await fetch(`${API}/leads/expert-review`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ society_id: society.society_id, note: "Needs help collecting redevelopment documents.", context: "document_vault" }),
        credentials: "include"
      });
      if (res.ok) toast.success("Thanks — our team will reach out to help you collect the rest.");
      else toast.error("Could not send request");
    } catch (e) { toast.error("Could not send request"); }
    finally { setLeadSending(false); }
  };

  const statusBadge = (status) => {
    switch (status) {
      case "verified":
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-100"><BadgeCheck className="w-3 h-3" /> Verified</span>;
      case "under_review":
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-100"><Clock className="w-3 h-3" /> Under Review</span>;
      case "uploaded":
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-100"><CheckCircle className="w-3 h-3" /> Uploaded</span>;
      default:
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 bg-slate-100 text-slate-500 border border-slate-200">Not Uploaded</span>;
    }
  };

  const sourceChip = (source) => (
    source === "mangohouse"
      ? <span className="px-2 py-0.5 rounded-full text-[11px] font-medium flex items-center gap-1 bg-indigo-50 text-indigo-700 border border-indigo-100"><ShieldCheck className="w-3 h-3" /> MangoHouse Team</span>
      : <span className="px-2 py-0.5 rounded-full text-[11px] font-medium flex items-center gap-1 bg-slate-100 text-slate-600 border border-slate-200"><Building2 className="w-3 h-3" /> Self-reported</span>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96"><div className="spinner"></div></div>
      </DashboardLayout>
    );
  }

  if (!society) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <NeedsSetup message="Set up your society to open its Document Vault." />
        </div>
      </DashboardLayout>
    );
  }

  if (!isAdminManaging && society.verification_status && society.verification_status !== "verified") {
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto py-16 text-center" data-testid="documents-locked">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Document Vault is Locked</h2>
          <p className="text-slate-600 mb-6">
            {society.verification_status === "pending_verification"
              ? "Your verification request is pending. Our team will connect with you soon to unlock the possibilities for your society."
              : "Claim this society to unlock document uploads and further services after verification from our team."}
          </p>
          <Button onClick={() => window.location.href = "/dashboard"} className="bg-emerald-700 hover:bg-emerald-800">Go to Dashboard</Button>
        </div>
      </DashboardLayout>
    );
  }

  const score = vault?.score ?? 0;
  const tier1Gaps = vault?.tier1_gaps ?? [];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="document-vault">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Document Vault</h1>
          <p className="text-slate-600">Make your society document-ready — upload each document into its own slot.</p>
          <p className="text-xs text-emerald-700 mt-1 font-medium">🛡️ Each Society, Safe Society — store your key documents (RCC plans, plans, deeds) securely, free.</p>
        </div>

        {/* Redevelopment Ready score */}
        <Card data-testid="redevelopment-ready-card">
          <CardContent className="py-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Redevelopment Ready</p>
                <p className="text-xs text-slate-500">Weighted by how much each document matters (critical documents count more).</p>
              </div>
              <span className="text-3xl font-bold text-emerald-700" data-testid="vault-score">{score}%</span>
            </div>
            <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-600 rounded-full transition-all" style={{ width: `${score}%` }} data-testid="vault-score-bar" />
            </div>
            {tier1Gaps.length > 0 && (
              <div className="mt-4 bg-red-50 border border-red-100 rounded-lg p-3" data-testid="tier1-gaps">
                <p className="text-xs font-semibold text-red-700 mb-1">Missing critical documents:</p>
                <p className="text-sm text-red-700">{tier1Gaps.join(", ")}</p>
              </div>
            )}
            {score < 100 && (
              <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                <p className="text-sm text-slate-600">Need help collecting the rest?</p>
                <Button variant="outline" size="sm" onClick={handleTalkToTeam} disabled={leadSending} data-testid="talk-to-mangohouse-btn">
                  {leadSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Talk to MangoHouse
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Slot groups */}
        {vault?.groups?.map((group) => (
          <Card key={group.key} data-testid={`vault-group-${group.key}`}>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <span>{GROUP_ICONS[group.icon] || "📁"}</span> {group.label}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {group.slots.map((slot) => (
                <div
                  key={slot.key}
                  className={`rounded-lg border p-3 ${slot.applicable ? "bg-white border-slate-200" : "bg-slate-50 border-dashed border-slate-200"}`}
                  data-testid={`vault-slot-${slot.key}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900">{slot.label}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] border ${TIER_META[slot.tier].cls}`}>{TIER_META[slot.tier].label}</span>
                        {slot.applicable ? statusBadge(slot.status) : (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-400 border border-slate-200">Not applicable yet</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {slot.conditional && (
                        <Button
                          variant="ghost" size="sm"
                          className="text-xs text-slate-500"
                          onClick={() => handleToggleApplicable(slot, !slot.applicable)}
                          data-testid={`toggle-applicable-${slot.key}`}
                        >
                          {slot.applicable ? "Mark N/A" : "Mark applicable"}
                        </Button>
                      )}
                      {slot.applicable && (
                        <Button
                          size="sm" variant="outline"
                          className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                          onClick={() => openUpload(slot)}
                          data-testid={`upload-slot-${slot.key}`}
                        >
                          <Upload className="w-3.5 h-3.5 mr-1" /> Upload
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* uploaded docs in this slot */}
                  {slot.documents?.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {slot.documents.map((doc) => (
                        <div key={doc.document_id} className="flex items-center gap-2 flex-wrap bg-slate-50 rounded-md px-3 py-2" data-testid={`doc-item-${doc.document_id}`}>
                          <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span className="text-sm text-slate-800 truncate max-w-[160px]">{doc.name}</span>
                          {sourceChip(doc.source)}
                          <span className="text-xs text-slate-400">
                            {doc.uploaded_by_name ? `by ${doc.uploaded_by_name}` : ""} {doc.uploaded_at ? `· ${new Date(doc.uploaded_at).toLocaleDateString()}` : ""}
                          </span>
                          <div className="flex items-center gap-1 ml-auto">
                            <Button size="sm" variant="ghost" className="text-slate-600 h-8 px-2" onClick={() => handleDownload(doc.document_id)} data-testid={`download-doc-${doc.document_id}`}>
                              <Download className="w-4 h-4" />
                            </Button>
                            {doc.ocr_status === "completed" && doc.extracted_text ? (
                              <Button size="sm" variant="ghost" className="text-blue-600 h-8 px-2" onClick={() => handleViewText(doc.document_id)} data-testid={`view-text-${doc.document_id}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button size="sm" variant="ghost" className="text-emerald-600 h-8 px-2" disabled={ocrProcessing === doc.document_id} onClick={() => handleRunOCR(doc.document_id)} data-testid={`run-ocr-${doc.document_id}`}>
                                {ocrProcessing === doc.document_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanText className="w-4 h-4" />}
                              </Button>
                            )}
                            {isTeam && doc.status !== "verified" && (
                              <Button size="sm" variant="ghost" className="text-emerald-700 h-8 px-2" onClick={() => handleSetStatus(doc.document_id, "verified")} data-testid={`verify-doc-${doc.document_id}`}>
                                <BadgeCheck className="w-4 h-4" />
                              </Button>
                            )}
                            {isTeam && doc.status === "uploaded" && (
                              <Button size="sm" variant="ghost" className="text-amber-600 h-8 px-2" onClick={() => handleSetStatus(doc.document_id, "under_review")} data-testid={`review-doc-${doc.document_id}`}>
                                <Clock className="w-4 h-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="text-red-600 h-8 px-2" onClick={() => handleDelete(doc.document_id)} data-testid={`delete-doc-${doc.document_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        ))}

        {/* Upload dialog */}
        <Dialog open={!!activeSlot} onOpenChange={(o) => !o && setActiveSlot(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope' }}>Upload — {activeSlot?.label}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <Label htmlFor="docname">Document Name</Label>
                <Input id="docname" value={docName} onChange={(e) => setDocName(e.target.value)} placeholder={activeSlot?.label} data-testid="doc-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${captureType === "file" && selectedFile ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-emerald-500"}`} onClick={() => fileInputRef.current?.click()} data-testid="file-upload-zone">
                  <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => handleFileSelect(e, "file")} />
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">Upload File</p>
                  <p className="text-xs text-slate-400">PDF, JPG, PNG</p>
                </div>
                <div className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${captureType === "camera" && selectedFile ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-emerald-500"}`} onClick={() => cameraInputRef.current?.click()} data-testid="camera-capture-zone">
                  <input ref={cameraInputRef} type="file" className="hidden" accept="image/*" capture="environment" onChange={(e) => handleFileSelect(e, "camera")} />
                  <Camera className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">Take Photo</p>
                  <p className="text-xs text-slate-400">Use Camera</p>
                </div>
              </div>

              {selectedFile && (
                <div className="p-3 bg-slate-50 rounded-lg flex items-center gap-3">
                  {previewUrl ? <img src={previewUrl} alt="Preview" className="w-16 h-16 object-cover rounded" /> : <div className="w-16 h-16 bg-slate-200 rounded flex items-center justify-center"><File className="w-6 h-6 text-slate-500" /></div>}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{selectedFile.name}</p>
                    <p className="text-xs text-slate-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => { setSelectedFile(null); setPreviewUrl(null); }}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              )}

              <label className="flex items-start gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
                <input type="checkbox" checked={tosAccepted} onChange={(e) => setTosAccepted(e.target.checked)} className="mt-0.5" data-testid="tos-accept-checkbox" />
                <span>
                  I understand these are sensitive legal documents. I accept ReDevelop's storage &amp; terms of use:
                  documents are stored securely for my society, kept separate from personal resident data, and
                  ReDevelop is not liable for the legal validity of uploaded documents.
                </span>
              </label>

              <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800" disabled={uploading || !selectedFile || !tosAccepted} data-testid="submit-doc-btn">
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Upload Document</>}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Extracted text dialog */}
        <Dialog open={textDialogOpen} onOpenChange={setTextDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <ScanText className="w-5 h-5 text-emerald-600" /> Extracted Text
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              {textViewDoc?.extracted_text ? (
                <div className="bg-slate-50 rounded-lg p-4 max-h-[50vh] overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-sm text-slate-700 font-mono">{textViewDoc.extracted_text}</pre>
                </div>
              ) : <p className="text-slate-500 text-center py-8">No extracted text available</p>}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => { navigator.clipboard.writeText(textViewDoc?.extracted_text || ""); toast.success("Copied"); }}>Copy Text</Button>
              <Button onClick={() => setTextDialogOpen(false)}>Close</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};
